import { Workbox } from 'workbox-window';

import { env } from '@/lib/env';
import { logger } from '@/lib/logging';
import { FRONTEND_UI_EVENTS } from '@blms/shared/telemetry/events';

type RegisterServiceWorkerOptions = {
  reload?: () => void;
  updateIntervalMs?: number;
};

export function registerServiceWorker(options: RegisterServiceWorkerOptions = {}) {
  if (!env.enableServiceWorker) return;
  if (!('serviceWorker' in navigator)) return;
  if (typeof window === 'undefined') return;

  const reload = options.reload ?? (() => window.location.reload());
  const wb = new Workbox('/sw.js', { scope: '/' });
  let hasReloadedForUpdate = false;

  wb.addEventListener('waiting', () => {
    // Activate updates immediately for installed PWAs that do not expose browser refresh UI.
    wb.messageSkipWaiting();
  });

  wb.addEventListener('controlling', () => {
    if (hasReloadedForUpdate) return;
    hasReloadedForUpdate = true;
    reload();
  });

  const updateIntervalMs = options.updateIntervalMs ?? 5 * 60 * 1000;
  const refreshRegistration = () => {
    if (document.visibilityState === 'hidden') return;
    void wb.update();
  };

  const intervalId = window.setInterval(refreshRegistration, updateIntervalMs);
  window.addEventListener('focus', refreshRegistration);
  document.addEventListener('visibilitychange', refreshRegistration);

  const cleanup = () => {
    window.clearInterval(intervalId);
    window.removeEventListener('focus', refreshRegistration);
    document.removeEventListener('visibilitychange', refreshRegistration);
  };

  wb.register({ immediate: true }).catch((error) => {
    cleanup();
    logger.error('[pwa] failed to register service worker', {
      event: FRONTEND_UI_EVENTS.serviceWorkerError,
      error,
    });
  });

  return cleanup;
}
